import { useMemo } from "react";
import _ from "lodash";
import { Circle, Search, Store, TrendingUp, X } from "lucide-react";
import { Card } from "../../../components";
import { ErrorPlaceholder } from "../../../components/common";
import { Col } from "../../../components/col";
import { Input } from "../../../components/input";
import { Row } from "../../../components/row";
import CommunityImpactMap from "./map/community-impact/CommunityImpactMap";
import { MapBodySkeleton, PartnerListSkeleton } from "./region/region-skeletons";
import { RegionState } from "./region/RegionState";
import type { AreaAnalysisModel } from "../types/AreaAnalysis";
import type { AreaModel } from "../types/Area";
import type { OrganizationMapConfig } from "../commons/Constant";
import type { MerchantModel } from "../types/Merchant";
import { useHeatMapMerchants } from "../hooks/useHeatMapMerchants";
import type { DashboardFilterPayload } from "../utils/heat-map.utils";

const UNFILTERED_MERCHANTS_PAYLOAD: DashboardFilterPayload = { filter: {} };

type CommunityImpactSectionProps = {
  areas: AreaModel[] | null;
  analysis: AreaAnalysisModel[] | null;
  organizationName: string;
  mapConfig?: OrganizationMapConfig;
  isMapLoading?: boolean;
  mapError?: string | null;
  onRetryMap?: () => void;
  selectedDistrict: AreaModel | null;
  onSelectDistrict: (district: AreaModel | null) => void;
  filterRevision?: number;
  merchantSearchQuery: string;
  setMerchantSearchQuery: React.Dispatch<React.SetStateAction<string>>;
};

export default function CommunityImpactSection({
  areas: heatMap,
  analysis: areaAnalysis,
  organizationName,
  mapConfig,
  isMapLoading = false,
  mapError,
  onRetryMap,
  selectedDistrict,
  onSelectDistrict,
  filterRevision,
  merchantSearchQuery,
  setMerchantSearchQuery,
}: CommunityImpactSectionProps) {
  const {
    data: merchantsData,
    loading: isLoadingPartners,
    error: partnersError,
    refetch: refetchPartners,
  } = useHeatMapMerchants(UNFILTERED_MERCHANTS_PAYLOAD);
  const allCommunityPartners = useMemo(() => merchantsData ?? [], [merchantsData]);
  const normalizedSearchQuery = merchantSearchQuery.trim().toLowerCase();

  const filteredAreas = useMemo(() => {
    const areaAnalysisById = new Map<string, AreaAnalysisModel>();

    (areaAnalysis || []).forEach((area) => {
      areaAnalysisById.set(area.organization_population_id, area);
    });

    const mappedAreas = (heatMap || []).map((area) => {
      const metrics = areaAnalysisById.get(area.organization_population_id);

      return {
        ...area,
        engagement: metrics?.engagement ?? area.engagement ?? 0,
        total_users: area.total_users ?? metrics?.civic_points_user ?? 0,
        population: area.population ?? metrics?.population ?? 0,
      };
    });

    if (!normalizedSearchQuery || selectedDistrict) {
      return mappedAreas;
    }

    return mappedAreas.filter((area) =>
      area.area_name.toLowerCase().includes(normalizedSearchQuery),
    );
  }, [areaAnalysis, heatMap, normalizedSearchQuery, selectedDistrict]);

  const filteredCommunityPartners = useMemo(() => {
    const partnersInNeighborhood = !selectedDistrict?.organization_population_id
      ? allCommunityPartners
      : allCommunityPartners.filter(
          (merchant) =>
            merchant.organization_population_id === selectedDistrict.organization_population_id,
        );

    if (!normalizedSearchQuery) {
      return partnersInNeighborhood;
    }

    return partnersInNeighborhood.filter((merchant) => {
      const partnerType = merchant.category?.name || merchant.organization_name || "";

      return (
        merchant.merchant_name.toLowerCase().includes(normalizedSearchQuery) ||
        merchant.address.toLowerCase().includes(normalizedSearchQuery) ||
        (merchant.email || "").toLowerCase().includes(normalizedSearchQuery) ||
        (merchant.website || "").toLowerCase().includes(normalizedSearchQuery) ||
        partnerType.toLowerCase().includes(normalizedSearchQuery)
      );
    });
  }, [allCommunityPartners, normalizedSearchQuery, selectedDistrict]);

  return (
    <Row className="h-[600px] items-stretch">
      <div className="w-9/12 border border-gray-200 dark:border-gray-700 rounded-2xl bg-white dark:bg-gray-800 flex flex-col">
        <Row className="px-5 pt-5 justify-between items-center">
          <Col className="gap-1 text-sm">
            <div className="flex">
              <Store
                className="mr-1"
                color="#00C49A"
              />
              <span className="text-gray-900 dark:text-gray-100">
                {organizationName} Community Impact Map
              </span>
            </div>
            <div className="text-gray-600 dark:text-gray-400">
              Monitor community partner activity, resident engagement, and neighborhood reach to
              identify areas of impact and opportunities for greater participation.
            </div>
          </Col>
        </Row>
        <Row className="px-5 pt-[15px] pb-[4px] justify-between items-center">
          <Col>
            <div className="w-80">
              <Input
                type="text"
                placeholder={
                  selectedDistrict ? "Search community partners...." : "Search neighborhoods...."
                }
                value={merchantSearchQuery}
                onChange={(e) => setMerchantSearchQuery(e.target.value)}
                startIcon={<Search className="h-4 w-4 text-gray-400 dark:text-gray-500" />}
                className="h-10 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 text-gray-900 dark:text-gray-100"
              />
            </div>
          </Col>
        </Row>
        <RegionState
          isLoading={isMapLoading}
          error={mapError}
          onRetry={onRetryMap}
          skeleton={<MapBodySkeleton />}
        >
          <div className="relative flex-1">
            <div className="absolute top-6 right-6 bg-white dark:bg-gray-800 shadow-md border border-gray-200 dark:border-gray-600 rounded-2xl px-4 py-3 text-sm z-[9]">
              <div className="font-semibold mb-2 flex items-center text-[#00BCD4] dark:text-cyan-400 text-[12px]">
                <TrendingUp
                  size={16}
                  className="mr-1"
                />
                <span>Community Engagement Classification</span>
              </div>
              <div className="flex items-center gap-2 mb-1">
                <span className="w-3 h-3 rounded-full bg-green-500"></span>
                <span className="text-[12px] text-gray-700 dark:text-gray-300">Strong (5%+)</span>
              </div>
              <div className="flex items-center gap-2 mb-1">
                <span className="w-3 h-3 rounded-full bg-yellow-400"></span>
                <span className="text-[12px] text-gray-700 dark:text-gray-300">
                  Moderate (2%-4.99%)
                </span>
              </div>
              <div className="flex items-center gap-2 mb-1">
                <span className="w-3 h-3 rounded-full bg-red-500"></span>
                <span className="text-[12px] text-gray-700 dark:text-gray-300">Low (&lt;2%)</span>
              </div>
            </div>
            <div className="absolute bottom-6 left-6 bg-white dark:bg-gray-800 shadow-md border-2 border-gray-200 dark:border-gray-600 rounded-xl px-2 py-1 text-[10px] z-[2]">
              <div className="flex justify-left items-center">
                <Circle
                  className="mr-2"
                  size={12}
                  fill="#00BCD4"
                  stroke="#00BCD4"
                />
                <div>
                  <span className="text-[#00BCD4] dark:text-cyan-400 text-sm block">
                    {organizationName}
                  </span>
                  <span className="block text-gray-700 dark:text-gray-300">
                    Shelby County, Tennessee
                  </span>
                </div>
              </div>
            </div>

            <CommunityImpactMap
              areas={filteredAreas}
              mapConfig={mapConfig}
              communityPartners={allCommunityPartners}
              selectedDistrict={selectedDistrict}
              filterRevision={filterRevision}
              onSelectArea={(area) => {
                if (!_.isNil(area)) {
                  onSelectDistrict(area);
                } else {
                  onSelectDistrict(null);
                }
              }}
            />
          </div>
        </RegionState>
      </div>

      <div className="w-3/12 h-full border border-gray-200 dark:border-gray-700 rounded-2xl bg-white dark:bg-gray-800 flex flex-col">
        <div className="w-full flex items-center justify-between p-5">
          <span className="text-md text-[#364153] dark:text-gray-200">Community Partners</span>
          {selectedDistrict && (
            <button
              type="button"
              aria-label="Clear neighborhood filter"
              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
              onClick={() => onSelectDistrict(null)}
            >
              <X size={16} />
            </button>
          )}
        </div>
        <CommunityImpactPartnerList
          merchants={filteredCommunityPartners}
          isLoading={isLoadingPartners}
          partnersError={partnersError}
          onRetryPartners={() => {
            void refetchPartners();
          }}
          neighborhoodName={selectedDistrict?.area_name}
        />
      </div>
    </Row>
  );
}

const HouseIcon = ({ color }: { color: string }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M1.6665 5.83342L5.3415 2.15842C5.49655 2.00244 5.68094 1.8787 5.88403 1.79431C6.08712 1.70993 6.30491 1.66657 6.52484 1.66675H13.4748C13.6948 1.66657 13.9126 1.70993 14.1156 1.79431C14.3187 1.8787 14.5031 2.00244 14.6582 2.15842L18.3332 5.83342"
      stroke={color}
      strokeWidth="1.66667"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M3.3335 10V16.6667C3.3335 17.1087 3.50909 17.5326 3.82165 17.8452C4.13421 18.1577 4.55814 18.3333 5.00016 18.3333H15.0002C15.4422 18.3333 15.8661 18.1577 16.1787 17.8452C16.4912 17.5326 16.6668 17.1087 16.6668 16.6667V10"
      stroke={color}
      strokeWidth="1.66667"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12.5 18.3333V14.9999C12.5 14.5579 12.3244 14.134 12.0118 13.8214C11.6993 13.5088 11.2754 13.3333 10.8333 13.3333H9.16667C8.72464 13.3333 8.30072 13.5088 7.98816 13.8214C7.6756 14.134 7.5 14.5579 7.5 14.9999V18.3333"
      stroke={color}
      strokeWidth="1.66667"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M1.6665 5.83325H18.3332"
      stroke={color}
      strokeWidth="1.66667"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M18.3332 5.83325V8.33325C18.3332 8.77528 18.1576 9.1992 17.845 9.51176C17.5325 9.82432 17.1085 9.99992 16.6665 9.99992C16.1796 9.97312 15.7146 9.78887 15.3415 9.47492C15.2421 9.40306 15.1225 9.36439 14.9998 9.36439C14.8772 9.36439 14.7576 9.40306 14.6582 9.47492C14.2851 9.78887 13.8201 9.97312 13.3332 9.99992C12.8463 9.97312 12.3813 9.78887 12.0082 9.47492C11.9087 9.40306 11.7892 9.36439 11.6665 9.36439C11.5438 9.36439 11.4243 9.40306 11.3248 9.47492C10.9517 9.78887 10.4867 9.97312 9.99984 9.99992C9.51295 9.97312 9.04795 9.78887 8.67484 9.47492C8.5754 9.40306 8.45585 9.36439 8.33317 9.36439C8.21049 9.36439 8.09094 9.40306 7.9915 9.47492C7.6184 9.78887 7.15339 9.97312 6.6665 9.99992C6.17962 9.97312 5.71461 9.78887 5.3415 9.47492C5.24207 9.40306 5.12252 9.36439 4.99984 9.36439C4.87716 9.36439 4.7576 9.40306 4.65817 9.47492C4.28506 9.78887 3.82006 9.97312 3.33317 9.99992C2.89114 9.99992 2.46722 9.82432 2.15466 9.51176C1.8421 9.1992 1.6665 8.77528 1.6665 8.33325V5.83325"
      stroke={color}
      strokeWidth="1.66667"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CommunityImpactPartnerList = ({
  merchants,
  isLoading,
  partnersError,
  onRetryPartners,
  neighborhoodName,
}: {
  merchants: MerchantModel[];
  isLoading: boolean;
  partnersError?: string | null;
  onRetryPartners?: () => void;
  neighborhoodName?: string;
}) => {
  if (isLoading) {
    return <PartnerListSkeleton />;
  }

  if (partnersError) {
    return (
      <div className="flex flex-1 flex-col justify-center px-2">
        <ErrorPlaceholder
          error={partnersError}
          onRetry={onRetryPartners}
        />
      </div>
    );
  }

  if (_.isEmpty(merchants)) {
    return (
      <div className="flex h-full w-full flex-col justify-center items-center text-gray-400 dark:text-gray-500 text-center p-3">
        <span className="text-lg font-semibold text-gray-600 dark:text-gray-300">
          No Partners Found
        </span>
        <span className="text-gray-500 dark:text-gray-400">
          {neighborhoodName
            ? `No community partners were returned for ${neighborhoodName}`
            : "Community partners will appear here when available"}
        </span>
      </div>
    );
  }

  return (
    <div className="flex-1 mt-1 overflow-y-auto p-4">
      {merchants.map((merchant) => (
        <CommunityImpactMerchantCard
          key={merchant.merchant_id}
          merchant={merchant}
        />
      ))}
    </div>
  );
};

const CommunityImpactMerchantCard = ({ merchant }: { merchant: MerchantModel }) => {
  const residentsEngaged = merchant.active_users ?? merchant.total_redemptions ?? 0;
  const activitiesCount = Math.max(0, merchant.total_coupons || 0);
  const partnerType = merchant.category?.name || merchant.organization_name || "-";

  return (
    <Card className="p-4 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-xl shadow-sm mb-4">
      <Row className="flex items-center gap-2 mb-2">
        <HouseIcon color="#00BCD4" />
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Partner Name
          </div>
          <span className="font-semibold text-[15px] text-gray-900 dark:text-gray-100">
            {merchant.merchant_name}
          </span>
        </div>
      </Row>

      <div className="mb-2 text-sm text-gray-600 dark:text-gray-400">
        <span className="font-semibold text-gray-900 dark:text-gray-100">Address:</span>{" "}
        {merchant.address}
      </div>
      <div className="mb-3 space-y-1 text-sm">
        <div className="text-gray-600 dark:text-gray-400 break-all">
          <span className="font-semibold text-gray-900 dark:text-gray-100">Email:</span>{" "}
          {merchant.email || "-"}
        </div>
        <div className="break-all">
          <span className="font-semibold text-gray-900 dark:text-gray-100">Website:</span>{" "}
          {merchant.website ? (
            <a
              href={merchant.website}
              target="_blank"
              rel="noreferrer"
              className="text-[#00BCD4] dark:text-cyan-400 hover:underline"
            >
              {merchant.website}
            </a>
          ) : (
            <span className="text-gray-600 dark:text-gray-400">-</span>
          )}
        </div>
      </div>
      <div className="border-t border-gray-200 dark:border-gray-600 pt-3 mt-3">
        <Row className="flex justify-between items-center mb-2">
          <span className="text-sm text-gray-700 dark:text-gray-300">Residents Engaged:</span>
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {residentsEngaged.toLocaleString()}
          </span>
        </Row>
        <Row className="flex justify-between items-center mb-2">
          <span className="text-sm text-gray-700 dark:text-gray-300">Activities Delivered:</span>
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {activitiesCount}
          </span>
        </Row>
        <Row className="flex justify-between items-center">
          <span className="text-sm text-gray-700 dark:text-gray-300">Partner Type:</span>
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 text-right">
            {partnerType}
          </span>
        </Row>
      </div>
    </Card>
  );
};
